import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Tooltip,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import QuestionForm from '../../components/QuestionForm/QuestionForm';
import {
    Add,
    Settings,
    Build,
    CheckBox,
    TextFields,
    List,
    CheckBoxOutlineBlank,
    Numbers,
    PercentOutlined,
    Extension,
    ArrowBack,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import TitleBar from '../../components/TitleBar/TitleBar';
import { 
    getReviewQuestionsByTypeAndFrequency, 
    getReviewEntityTypes, 
    getReviewFrequencies, 
    updateReviewQuestion, 
    deleteReviewQuestion, 
    type UpdateReviewQuestionPayload,
    type ReviewEntityType,
    type ReviewFrequency 
} from '../../services/api';
import { ReviewQuestion } from '../../types';
import { getUser } from '../../services/auth';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const getAnswerTypeIcon = (answerType: string) => {
    switch (answerType) {
        case 'YES-NO':
            return <CheckBox />;
        case 'TEXT':
            return <TextFields />;
        case 'SELECT':
            return <List />;
        case 'MULTI-SELECT':
            return <CheckBoxOutlineBlank />;
        case 'NUMERIC':
            return <Numbers />;
        case 'PERCENTAGE':
            return <PercentOutlined />;
        default:
            return null;
    }
};

const getQuestionTypeIcon = (questionType: string) => {
    return questionType === 'PRODUCT_DEFAULT' ? 
        <Settings color="primary" /> : 
        <Extension color="secondary" />;
};

const isNaN = (value: string | null | undefined): boolean => {
    if (!value) return true;
    return value.toLowerCase() === 'nan';
};

const formatDescription = (description: string | null | undefined): string => {
    if (isNaN(description)) return '-';
    return description || '-';
};

const Questionnaire = () => {
    const { entityTypeId, frequency } = useParams<{ entityTypeId: string; frequency: string }>();
    const navigate = useNavigate();
    const [value, setValue] = useState(0);
    const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
    const [entityTypes, setEntityTypes] = useState<ReviewEntityType[]>([]);
    const [entityTypeDisplayLabel, setEntityTypeDisplayLabel] = useState('');
    const [frequencyDisplayLabel, setFrequencyDisplayLabel] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const user = getUser();
    const isAdmin = user?.roles.some(role => role.role_name === 'ADMIN');
    const [editQuestion, setEditQuestion] = useState<ReviewQuestion | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!entityTypeId || !frequency) {
                    throw new Error('Entity type or frequency is missing');
                }

                const decodedEntityType = decodeURIComponent(entityTypeId);
                const decodedFrequency = decodeURIComponent(frequency);

                const [questionsRes, entityTypesRes, frequenciesRes] = await Promise.all([
                    getReviewQuestionsByTypeAndFrequency(
                        decodedEntityType,
                        decodedFrequency
                    ),
                    getReviewEntityTypes(),
                    getReviewFrequencies()
                ]);

                setQuestions(questionsRes.data);
                setEntityTypes(entityTypesRes.data);

                const currentEntityType = entityTypesRes.data.find(
                    (type) => type.name === decodedEntityType
                );
                
                const currentFrequency = frequenciesRes.data.find(
                    (freq) => freq.name === decodedFrequency
                );
                    
                setEntityTypeDisplayLabel(currentEntityType?.display_label || decodedEntityType);
                setFrequencyDisplayLabel(currentFrequency?.display_label || decodedFrequency);
            } catch (err) {
                setError('Failed to load questions');
                console.error('Error fetching questions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [entityTypeId, frequency]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const getQuestionsForDimension = (dimension: string) => {
        return questions.filter(q => q.review_dimension === dimension);
    };

    const handleAddQuestion = () => {
        navigate('/add-question');
    };

    const handleBack = () => {
        navigate('/entity-types');
    };

    const handleEditClick = (question: ReviewQuestion) => {
        setEditQuestion(question);
        setOpen(true);
    };

    const handleDeleteClick = (questionId: number) => {
        setSelectedQuestionId(questionId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedQuestionId && entityTypeId && frequency) {
            try {
                await deleteReviewQuestion(selectedQuestionId);
                // Refresh questions after delete
                const questionsRes = await getReviewQuestionsByTypeAndFrequency(
                    decodeURIComponent(entityTypeId),
                    decodeURIComponent(frequency)
                );
                setQuestions(questionsRes.data);
                setDeleteConfirmOpen(false);
                setSelectedQuestionId(null);
            } catch (err) {
                console.error('Error deleting question:', err);
                setError('Failed to delete question');
            }
        }
    };

    const handleSubmit = async (formData: any) => {
        if (!entityTypeId || !frequency) {
            setError('Entity type or frequency is missing');
            return;
        }

        try {
            if (editQuestion) {
                // Update existing question
                const payload: UpdateReviewQuestionPayload = {
                    question_id: editQuestion.id,
                    review_question_text: formData.questionText,
                    description: formData.description || undefined,
                    review_dimension_id: formData.dimension ? parseInt(formData.dimension) : undefined,
                    answer_type: formData.answerType,
                    answer_options: formData.answerOptions || undefined,
                    action_required: formData.actionRequired || undefined,
                    default_value: formData.defaultValue || undefined,
                    ideal_value: formData.idealValue || undefined
                };
                await updateReviewQuestion(payload);
            } else {
                // Existing code for adding new question
                // ...
            }
            
            // Refresh questions after update
            const questionsRes = await getReviewQuestionsByTypeAndFrequency(
                decodeURIComponent(entityTypeId),
                decodeURIComponent(frequency)
            );
            setQuestions(questionsRes.data);
            setOpen(false);
            setEditQuestion(null);
        } catch (err) {
            console.error('Error submitting question:', err);
            setError('Failed to save question');
        }
    };

    return (
        <Box>
            <TitleBar />
            <Container sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h4" component="div">
                            {entityTypeDisplayLabel}
                        </Typography>
                        <Chip 
                            label={frequencyDisplayLabel}
                            color="primary"
                            sx={{ 
                                fontSize: '1rem',
                                height: '32px'
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<ArrowBack />}
                            onClick={handleBack}
                            sx={{
                                borderWidth: 2,
                                '&:hover': {
                                    borderWidth: 2,
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                }
                            }}
                        >
                            Back
                        </Button>
                        {isAdmin && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={handleAddQuestion}
                            >
                                Add Question
                            </Button>
                        )}
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {loading ? (
                    <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                        <Typography variant="h6" color="text.secondary">
                            Loading...
                        </Typography>
                    </Paper>
                ) : questions.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                        <Typography variant="h6" color="text.secondary">
                            No questionnaire has been defined for {entityTypeDisplayLabel} yet.
                        </Typography>
                        {isAdmin && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={handleAddQuestion}
                                sx={{ mt: 2 }}
                            >
                                Add First Question
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={value}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    '& .MuiTab-root': {
                                        color: 'rgba(0, 0, 0, 0.4)', // lighter color for unselected tabs
                                    },
                                    '& .Mui-selected': {
                                        color: 'rgba(0, 0, 0, 0.87) !important', // darker color for selected tab
                                        fontWeight: 'bold',
                                    },
                                }}
                            >
                                {Array.from(new Set(questions.map(q => q.review_dimension))).map((dimension, index) => (
                                    <Tab 
                                        key={dimension} 
                                        label={dimension} 
                                        id={`tab-${index}`}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        {Array.from(new Set(questions.map(q => q.review_dimension))).map((dimension, index) => (
                            <TabPanel key={dimension} value={value} index={index}>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    '& th': {
                                                        backgroundColor: 'grey.100',
                                                        color: 'text.primary',
                                                        fontWeight: 600,
                                                        height: '36px',
                                                        padding: '8px 16px',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            >
                                                {isAdmin && (
                                                    <TableCell
                                                        sx={{
                                                            width: '5%'
                                                        }}
                                                    >
                                                        Type
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    sx={{
                                                        width: '40%'
                                                    }}
                                                >
                                                    Question
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '40%'
                                                    }}
                                                >
                                                    Description
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '10%'
                                                    }}
                                                >
                                                    Answer Type
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '15%'
                                                    }}
                                                >
                                                    Default Value
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '15%'
                                                    }}
                                                >
                                                    Ideal Value
                                                </TableCell>
                                                {isAdmin && (
                                                    <TableCell
                                                        sx={{
                                                            width: '10%'
                                                        }}
                                                    >
                                                        Actions
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getQuestionsForDimension(dimension).map((question, index) => (
                                                <TableRow
                                                    key={question.id}
                                                    sx={{
                                                        '&:nth-of-type(odd)': {
                                                            backgroundColor: 'grey.50'
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover',
                                                            transition: 'background-color 0.2s ease'
                                                        },
                                                        '& td': {
                                                            padding: '8px 16px'
                                                        }
                                                    }}
                                                >
                                                    {isAdmin && (
                                                        <TableCell>
                                                            <Tooltip title={question.review_question_type}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    {getQuestionTypeIcon(question.review_question_type)}
                                                                </Box>
                                                            </Tooltip>
                                                        </TableCell>
                                                    )}
                                                    <TableCell>{question.review_question_text}</TableCell>
                                                    <TableCell>{formatDescription(question.description)}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {getAnswerTypeIcon(question.answer_type)}
                                                            {question.answer_type}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{formatDescription(question.default_value)}</TableCell>
                                                    <TableCell>{formatDescription(question.ideal_value)}</TableCell>
                                                    {isAdmin && (
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Tooltip title="Edit Question">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEditClick(question)}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete Question">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteClick(question.id)}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </TabPanel>
                        ))}
                    </>
                )}
            </Container>

            {/* Add/Edit Question Dialog */}
            <Dialog 
                open={open} 
                onClose={() => {
                    setOpen(false);
                    setEditQuestion(null);
                }} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    {editQuestion ? 'Edit Question' : 'Add New Question'}
                </DialogTitle>
                <DialogContent>
                    <QuestionForm
                        initialData={editQuestion || undefined}
                        onSubmit={handleSubmit}
                        onCancel={() => {
                            setOpen(false);
                            setEditQuestion(null);
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteConfirmOpen} 
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this question? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Questionnaire;